import { socketClient, onConnect } from "~utils/websocket.ts";
import localforage from "localforage";

export type AppTab = {
	title: string;
	component: object;
	fullPath: string;
	props?: object;
};

const previousTab = shallowRef<AppTab | null>(null);
// @ts-ignore stom gezanik
const activeTab: ShallowRef<AppTab> = useStorageAsync<AppTab | null>(
	"pulsefood-active-tab",
	null as AppTab | null,
	localforage,
	{
		shallow: true,
		serializer: {
			read: (value) => JSON.parse(value) as AppTab | null,
			write: (value) => JSON.stringify(value),
		},
	},
);

// @ts-ignore stom gezanik
const sessionTabs: ShallowRef<AppTab[]> = useStorageAsync<AppTab[]>("pulsefood-session-tabs", [], localforage, {
	shallow: true,
	serializer: {
		read: (value) => JSON.parse(value) as AppTab | null,
		write: (value) => JSON.stringify(value),
	},
});
let tabsInitialized = false;

import { defineAsyncComponent, h, readonly, ShallowRef } from "vue";
import router from "~/plugins/VueRouter.ts";
import { useUserStore } from "~stores/User.ts";
import { hideAllPoppers } from "floating-vue";

// onConnect((frame) => {
// 	watch(sessionTabs, () => {
// 		if (!tabsInitialized) return;
//
// 		socketClient.publish({ destination: "/app/user.tabs", body: JSON.stringify(sessionTabs.value) });
// 	});
// });

if (!tabsInitialized) {
	watch(
		sessionTabs,
		() => {
			[...sessionTabs.value, activeTab.value].forEach(async (tab) => {
				if (tab === null) return;
				if (Object.keys(tab.component).includes("render")) return;

				const route = router.resolve(tab.fullPath);

				const comp = route.matched[0].components.default;
				let component = comp;

				if (comp instanceof Function) {
					component = defineAsyncComponent(comp);
				}

				tab.component = h(component, tab.props);
				tabsInitialized = true;
			});
		},
		{ immediate: sessionTabs.value.length > 0, once: true },
	);
}

/**
 * Composable function to manage the application tabs
 * Tabs are added by vue-router navigation guards
 */
export const useAppTabs = () => {
	// const { push, currentRoute, back, previousUrl } = useRouter();
	const { activeUser } = storeToRefs(useUserStore());

	const addTab = (tab: AppTab) => {
		previousTab.value = activeTab.value;
		activeTab.value = tab;

		if (activeUser.value?.settings?.tabDropdown == true) {
			sessionTabs.value = [tab, ...sessionTabs.value];
		} else {
			sessionTabs.value = [...sessionTabs.value, tab];
		}
	};

	/**
	 * @param navigate - Whether to automatically navigate to the previous tab after closing the current tab or to '/' if there are no tabs left
	 */
	const removeTab = (index: number, navigate: boolean = true) => {
		const closedActiveTab = activeTab.value.fullPath === sessionTabs.value[index].fullPath;
		sessionTabs.value = sessionTabs.value.toSpliced(index, 1);

		if (!closedActiveTab) {
			return;
		}

		if (previousTab.value && sessionTabs.value.some((tab) => tab.fullPath === previousTab.value.fullPath)) {
			activeTab.value = previousTab.value;
			previousTab.value = null;
			return;
		}

		if (sessionTabs.value.length === 0) {
			activeTab.value = null;
		} else {
			activeTab.value = sessionTabs.value[0];
		}

		previousTab.value = null;
	};

	const setCurrentTabTitle = (...args: string[]) => {
		const tab = activeTab.value;

		setTabTitle(tab?.fullPath, ...args);
	};

	const setTabTitle = (fullPath: string | null, ...args: string[]) => {
		if (!fullPath) {
			return;
		}
		sessionTabs.value.find((t) => t.fullPath === fullPath)!.title = args.join(" ");
		sessionTabs.value = [...sessionTabs.value];
	};

	/**
	 * @param navigate - Whether to automatically navigate to the previous tab after closing the current tab or to '/' if there are no tabs left
	 */
	const closeCurrentTab = async (navigate: boolean = true) => {
		hideAllPoppers();

		const index = sessionTabs.value.findIndex((t) => t.fullPath === activeTab.value?.fullPath);
		if (index !== -1) {
			await removeTab(index, navigate);
		}
	};

	const activateTab = (tab: AppTab) => {
		hideAllPoppers();

		previousTab.value = activeTab.value;
		activeTab.value = sessionTabs.value.find((it) => it.fullPath === tab.fullPath);
	};

	const goToHome = () => {
		hideAllPoppers();

		activeTab.value = null;
	};

	const activatePreviousTab = () => {
		hideAllPoppers();

		const currentTab = activeTab.value;
		activeTab.value = previousTab.value;
		previousTab.value = currentTab;
	};

	return {
		sessionTabs,
		setCurrentTabTitle,
		setTabTitle,
		removeTab,
		activeTab: readonly(activeTab),
		previousTab: readonly(previousTab),
		addTab,
		activateTab,
		activatePreviousTab,
		closeCurrentTab,
		goToHome,
	};
};
